// client/src/components/indicadores/AgendamentosReport.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // ✅ USANDO API PADRONIZADA
import useAlert from '../../hooks/useAlert';
import { useAuth } from '../../context/auth/AuthProvider';
import { useAgendamentosReport } from '../../hooks/useReports';
import PageHeader from '../layout/PageHeader';
import { CalendarClock, Filter as FilterIcon, Smartphone, Globe, Users, Monitor } from 'lucide-react';
import KpiCard from '../dashboard/KpiCard';
import DoughnutChart from '../dashboard/DoughnutChart';
import BarChart from '../dashboard/BarChart';
import FilterBar from './FilterBar';
import { format } from 'date-fns';

// ✅ DEFINIR FILTROS INICIAIS COMO CONSTANTE
const initialFilterState = {
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(),
    unidadeId: '',
    profissionalId: '',
    servicoId: '',
    recursoId: '',
    origemAgendamento: '',
    tipoAgendamento: '',
    plataforma: ''
};

const AgendamentosReport = () => {
    const { setAlert } = useAlert();
    const { user, scope } = useAuth();
    
    const [filters, setFilters] = useState(initialFilterState);
    const [appliedFilters, setAppliedFilters] = useState(initialFilterState);
    const [showFilters, setShowFilters] = useState(false);
    const [filterOptions, setFilterOptions] = useState({
        unidades: [],
        profissionais: [],
        servicos: [],
        recursos: [],
        origens: [
            { value: '', label: 'Todas as Origens' },
            { value: 'manual', label: 'Manual (Admin)' },
            { value: 'site', label: 'Site' },
            { value: 'app', label: 'Aplicativo' }
        ],
        tipos: [
            { value: '', label: 'Todos os Tipos' },
            { value: 'manual', label: 'Manual por Usuário' },
            { value: 'online_logado', label: 'Online (Cliente Logado)' },
            { value: 'online_nao_logado', label: 'Online (Cliente Não Logado)' }
        ],
        plataformas: [
            { value: '', label: 'Todas as Plataformas' },
            { value: 'web_admin', label: 'Painel Administrativo' },
            { value: 'web_cliente', label: 'Site do Cliente' },
            { value: 'ios', label: 'iOS' },
            { value: 'android', label: 'Android' }
        ]
    });
    const [loadingFilters, setLoadingFilters] = useState(false);

    // 🔥 USANDO HOOK - useAgendamentosReport
    const { 
        data: reportData, 
        isLoading,
        error 
    } = useAgendamentosReport(appliedFilters);

    // ✅ TRATAMENTO DE ERRO PADRONIZADO
    useEffect(() => {
        if (error) {
            console.error('❌ Erro no relatório de agendamentos:', error);
            
            let errorMessage = 'Ocorreu um erro ao buscar o relatório de agendamentos.';
            
            if (error.message.includes('Servidor indisponível')) {
                errorMessage = error.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setAlert(errorMessage, 'danger');
        }
    }, [error, setAlert]);

    // 🔥 CARREGAR OPÇÕES DE FILTRO - PADRONIZADO
    useEffect(() => {
        const fetchFilterOptions = async () => {
            // ✅ CORREÇÃO: só bloqueava sem user.account — franquia_admin e
            // matriz_admin nunca viam essas opções de filtro. Os endpoints
            // já escopam sozinhos pelo JWT (filterByUserUnits).
            if (!user?.account?._id && !user?.franquia?._id && scope !== 'matriz') {
                console.log('⚠️ Usuário sem conta, franquia ou escopo matriz — sem opções de filtro');
                return;
            }

            setLoadingFilters(true);

            try {
                const accountId = user?.account?._id;
                const baseParams = accountId ? { account: accountId, limit: 1000 } : { limit: 1000 };
                console.log('🔄 Carregando filtros de agendamentos', baseParams);

                // ✅ PADRONIZADO: Usando a mesma estrutura dos outros componentes
                const requests = [
                    // Unidades
                    api.get('/unidades', { params: baseParams }),
                    // Profissionais
                    api.get('/users', { params: { ...baseParams, role: 'profissional' } }),
                    // Serviços
                    api.get('/servicos', { params: baseParams }),
                    // Recursos
                    api.get('/recursos', { params: baseParams })
                ];

                console.log('📡 Fazendo requisições para filtros...');
                
                const [unidadesRes, profissionaisRes, servicosRes, recursosRes] = await Promise.all(requests);

                // ✅ PADRONIZADO: Mesma estrutura de extração de dados
                const filterData = {
                    unidades: unidadesRes.data?.data || unidadesRes.data || [],
                    profissionais: profissionaisRes.data?.data || profissionaisRes.data || [],
                    servicos: servicosRes.data?.data || servicosRes.data || [],
                    recursos: recursosRes.data?.data || recursosRes.data || [],
                    origens: [
                        { value: '', label: 'Todas as Origens' },
                        { value: 'manual', label: 'Manual (Admin)' },
                        { value: 'site', label: 'Site' },
                        { value: 'app', label: 'Aplicativo' }
                    ],
                    tipos: [
                        { value: '', label: 'Todos os Tipos' },
                        { value: 'manual', label: 'Manual por Usuário' },
                        { value: 'online_logado', label: 'Online (Cliente Logado)' },
                        { value: 'online_nao_logado', label: 'Online (Cliente Não Logado)' }
                    ],
                    plataformas: [
                        { value: '', label: 'Todas as Plataformas' },
                        { value: 'web_admin', label: 'Painel Administrativo' },
                        { value: 'web_cliente', label: 'Site do Cliente' },
                        { value: 'ios', label: 'iOS' },
                        { value: 'android', label: 'Android' }
                    ]
                };

                setFilterOptions(filterData);

                console.log('✅ Filtros de agendamentos carregados com sucesso:', {
                    unidades: filterData.unidades.length,
                    profissionais: filterData.profissionais.length,
                    servicos: filterData.servicos.length,
                    recursos: filterData.recursos.length
                });

            } catch (error) {
                console.error('❌ Erro ao carregar filtros de agendamentos:', error);
                
                let errorMessage = 'Erro ao carregar opções de filtro.';
                
                if (error.message.includes('Servidor indisponível')) {
                    errorMessage = 'Servidor backend indisponível. Verifique se está rodando.';
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                setAlert(errorMessage, 'danger');
                
                // ✅ FALLBACK: Manter opções estáticas e arrays vazios
                setFilterOptions(prev => ({
                    ...prev,
                    unidades: [],
                    profissionais: [],
                    servicos: [],
                    recursos: []
                }));
            } finally {
                setLoadingFilters(false);
            }
        };
        
        fetchFilterOptions();
    }, [user, scope, setAlert]);

    const handleApplyFilters = () => {
        setAppliedFilters(filters);
        setShowFilters(false);
    };

    const handleClearFilters = () => {
        setFilters(initialFilterState);
        setAppliedFilters(initialFilterState);
    };

    // ✅ VERIFICAÇÃO DE CONEXÃO COM BACKEND
    const isBackendUnavailable = error?.message?.includes('Servidor indisponível');

    // 🔥 PREPARAÇÃO DOS DADOS PARA GRÁFICOS E KPIs
    const totalAgendamentos = reportData?.totalGeral || reportData?.resumoStatus?.reduce((sum, item) => sum + item.count, 0) || 0;
    
    // ✅ Dados de origem e plataforma
    const agendamentosPorOrigem = reportData?.porOrigem || [];
    const agendamentosPorPlataforma = reportData?.porPlataforma || [];
    const agendamentosPorTipo = reportData?.porTipo || [];
    
    // Gráfico de status
    const statusChartData = {
        labels: reportData?.resumoStatus?.map(item => item._id) || [],
        datasets: [{ 
            data: reportData?.resumoStatus?.map(item => item.count) || [], 
            backgroundColor: ['#3b82f6', '#ef4444', '#f59e0b', '#16a34a', '#6b7280'] 
        }],
    };
    
    // ✅ CORREÇÃO: Gráfico de evolução diária com números inteiros
    const dailyEvolutionData = reportData?.evolucaoDiaria || [];
    const dailyEvolutionChartData = {
        labels: dailyEvolutionData.map(item => format(new Date(item._id), 'dd/MM')) || [],
        datasets: [{ 
            label: 'Agendamentos por Dia', 
            data: dailyEvolutionData.map(item => item.count) || [], 
            backgroundColor: 'rgba(59, 130, 246, 0.7)',
            borderColor: 'rgba(59, 130, 246, 1)',
            borderWidth: 2
        }],
    };

    // ✅ CORREÇÃO: Opções do gráfico de barras para números inteiros
    const barChartOptions = {
        responsive: true,
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    callback: function(value) {
                        return Number.isInteger(value) ? value : '';
                    }
                }
            }
        },
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Evolução Diária de Agendamentos',
                font: { size: 16 },
            },
        },
    };

    // ✅ Gráfico de origem dos agendamentos
    const origemChartData = {
        labels: agendamentosPorOrigem.map(item => {
            const origens = {
                'manual': 'Manual',
                'site': 'Site',
                'app': 'App'
            };
            return origens[item._id] || item._id;
        }),
        datasets: [{ 
            data: agendamentosPorOrigem.map(item => item.total), 
            backgroundColor: ['#8b5cf6', '#06b6d4', '#10b981'] 
        }],
    };

    // ✅ Gráfico de plataformas
    const plataformaChartData = {
        labels: agendamentosPorPlataforma.map(item => {
            const plataformas = {
                'web_admin': 'Painel Admin',
                'web_cliente': 'Site Cliente',
                'ios': 'iOS',
                'android': 'Android',
                'null': 'Não Especificado'
            };
            return plataformas[item._id] || item._id;
        }),
        datasets: [{ 
            data: agendamentosPorPlataforma.map(item => item.total), 
            backgroundColor: ['#f59e0b', '#ef4444', '#3b82f6', '#10b981', '#6b7280'] 
        }],
    };

    // ✅ CORREÇÃO: Opções para gráficos circulares menores
    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    boxWidth: 12,
                    font: {
                        size: 11
                    }
                }
            },
            title: {
                display: true,
                font: { 
                    size: 14
                },
            },
        },
        cutout: '50%',
    };

    // ✅ KPIs de origem
    const getOrigemKpis = () => {
        const manual = agendamentosPorOrigem.find(item => item._id === 'manual')?.total || 0;
        const site = agendamentosPorOrigem.find(item => item._id === 'site')?.total || 0;
        const app = agendamentosPorOrigem.find(item => item._id === 'app')?.total || 0;
        
        return {
            manual,
            site,
            app,
            totalOnline: site + app,
            percentualOnline: totalAgendamentos > 0 ? ((site + app) / totalAgendamentos * 100).toFixed(1) : 0
        };
    };

    const origemKpis = getOrigemKpis();

    // ✅ LOADING STATE PADRONIZADO
    if (loadingFilters) {
        return (
            <div className="page-management-container report-page">
                <PageHeader 
                    icon={<CalendarClock size={24} />} 
                    title="Relatório de Agendamentos"
                />
                <div className="content-panel">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                        <p className="mt-3 text-muted">Carregando opções de filtro...</p>
                    </div>
                </div>
            </div>
        );
    }

    const pageActions = (
        <button 
            onClick={() => setShowFilters(prevState => !prevState)} 
            className="action-button action-button-secondary"
        >
            <FilterIcon size={18} className="icon"/> 
            {showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}
        </button>
    );

    return (
        <div className="page-management-container">
            <PageHeader 
                icon={<CalendarClock size={24} />} 
                title="Relatório de Agendamentos"
                actions={pageActions}
            />
            
            {/* 🔥 MENSAGEM DE ERRO DE CONEXÃO - PRIORIDADE MÁXIMA */}
            {isBackendUnavailable && (
                <div className="content-panel mb-4">
                    <div className="alert alert-danger">
                        <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <h5>❌ Erro de Conexão com o Servidor</h5>
                                <p className="mb-2">{error.message}</p>
                                <small className="text-muted">
                                    <strong>Para resolver:</strong><br/>
                                    1. Verifique se o servidor backend está rodando<br/>
                                    2. Confirme se a API está respondendo<br/>
                                    3. Verifique o comando: <code>npm run dev</code> no backend<br/>
                                    4. Recarregue a página após iniciar o servidor
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showFilters && (
                <div className="content-panel">
                    <h3 className="panel-title">Filtros</h3>
                    <FilterBar 
                        filters={filters}
                        setFilters={setFilters}
                        onApply={handleApplyFilters}
                        onClear={handleClearFilters}
                        options={filterOptions}
                        enabledFilters={['date', 'unidade', 'profissional', 'servico', 'recurso', 'origem', 'tipo', 'plataforma']}
                        isLoading={isLoading || loadingFilters}
                    />
                </div>
            )}

            {!isBackendUnavailable && appliedFilters && (
                <>
                    {/* 🔥 KPIs PRINCIPAIS - COM ÍCONES RESTAURADOS */}
                    <div className="dashboard-grid" style={{ marginTop: '1.5rem' }}>
                        <KpiCard 
                            title="Total de Agendamentos" 
                            value={totalAgendamentos} 
                            isLoading={isLoading}
                            icon={CalendarClock}
                            subtitle="Período selecionado"
                        />
                        
                        <KpiCard 
                            title="Agendamentos Online" 
                            value={origemKpis.totalOnline} 
                            subtitle={`${origemKpis.percentualOnline}% do total`}
                            isLoading={isLoading}
                            icon={Globe}
                        />
                        
                        <KpiCard 
                            title="Via Site" 
                            value={origemKpis.site} 
                            isLoading={isLoading}
                            icon={Monitor}
                        />
                        
                        <KpiCard 
                            title="Via App" 
                            value={origemKpis.app} 
                            isLoading={isLoading}
                            icon={Smartphone}
                        />
                    </div>

                    {/* 🔥 GRÁFICOS PRINCIPAIS - COM CORREÇÕES */}
                    <div className="row" style={{ marginTop: '1.5rem' }}>
                        <div className="col-lg-6 mb-4">
                            <div className="chart-container">
                                <BarChart 
                                    title="Evolução Diária de Agendamentos" 
                                    data={dailyEvolutionChartData}
                                    options={barChartOptions}
                                />
                            </div>
                        </div>
                        <div className="col-lg-3 mb-4">
                            <div className="chart-container" style={{ height: '300px' }}>
                                <DoughnutChart 
                                    title="Agendamentos por Status" 
                                    data={statusChartData}
                                    options={doughnutOptions}
                                />
                            </div>
                        </div>
                        <div className="col-lg-3 mb-4">
                            <div className="chart-container" style={{ height: '300px' }}>
                                <DoughnutChart 
                                    title="Agendamentos por Origem" 
                                    data={origemChartData}
                                    options={doughnutOptions}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 🔥 GRÁFICO DE PLATAFORMA (se houver dados) */}
                    {agendamentosPorPlataforma.length > 0 && (
                        <div className="row" style={{ marginTop: '1.5rem' }}>
                            <div className="col-lg-6 mb-4">
                                <div className="chart-container" style={{ height: '300px' }}>
                                    <DoughnutChart 
                                        title="Distribuição por Plataforma" 
                                        data={plataformaChartData}
                                        options={doughnutOptions}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 🔥 ANÁLISE DETALHADA */}
                    <div className="row" style={{ marginTop: '1.5rem' }}>
                        {/* Estatísticas por Origem */}
                        <div className="col-lg-4 mb-4">
                            <div className="stat-detail-card">
                                <h6>Distribuição por Origem</h6>
                                {agendamentosPorOrigem.length > 0 ? (
                                    agendamentosPorOrigem.map((origem, index) => {
                                        const label = {
                                            'manual': 'Manual (Admin)',
                                            'site': 'Site',
                                            'app': 'Aplicativo'
                                        }[origem._id] || origem._id;
                                        
                                        return (
                                            <div key={origem._id} className="stat-detail-item">
                                                <span>{label}</span>
                                                <strong>{origem.total} ({origem.percentual}%)</strong>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-center text-muted py-3">
                                        Nenhum dado disponível
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Estatísticas por Plataforma */}
                        <div className="col-lg-4 mb-4">
                            <div className="stat-detail-card">
                                <h6>Distribuição por Plataforma</h6>
                                {agendamentosPorPlataforma.length > 0 ? (
                                    agendamentosPorPlataforma.map((plataforma, index) => {
                                        const label = {
                                            'web_admin': 'Painel Admin',
                                            'web_cliente': 'Site Cliente',
                                            'ios': 'iOS',
                                            'android': 'Android',
                                            'null': 'Não Especificado'
                                        }[plataforma._id] || plataforma._id;
                                        
                                        return (
                                            <div key={plataforma._id} className="stat-detail-item">
                                                <span>{label}</span>
                                                <strong>{plataforma.total} ({plataforma.percentual}%)</strong>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-center text-muted py-3">
                                        Nenhum dado disponível
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Estatísticas por Tipo */}
                        <div className="col-lg-4 mb-4">
                            <div className="stat-detail-card">
                                <h6>Distribuição por Tipo</h6>
                                {agendamentosPorTipo.length > 0 ? (
                                    agendamentosPorTipo.map((tipo, index) => {
                                        const label = {
                                            'manual': 'Manual por Usuário',
                                            'online_logado': 'Online (Cliente Logado)',
                                            'online_nao_logado': 'Online (Cliente Não Logado)'
                                        }[tipo._id] || tipo._id;
                                        
                                        return (
                                            <div key={tipo._id} className="stat-detail-item">
                                                <span>{label}</span>
                                                <strong>{tipo.total} ({tipo.percentual}%)</strong>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-center text-muted py-3">
                                        Nenhum dado disponível
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 🔥 RESUMO ANALÍTICO */}
                    <div className="payment-analysis" style={{ marginTop: '1.5rem' }}>
                        <h4 className="panel-title mb-3">Análise do Período</h4>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="analysis-item">
                                    <div className="analysis-icon">
                                        <Globe size={20} className="text-primary" />
                                    </div>
                                    <div className="analysis-content">
                                        <h6>Taxa de Agendamentos Online</h6>
                                        <div className="analysis-value">
                                            <strong className={origemKpis.percentualOnline > 50 ? 'text-success' : 'text-warning'}>
                                                {origemKpis.percentualOnline}%
                                            </strong>
                                            <div className="analysis-meta">
                                                {origemKpis.totalOnline} de {totalAgendamentos} agendamentos
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="analysis-item">
                                    <div className="analysis-icon">
                                        <CalendarClock size={20} className="text-primary" />
                                    </div>
                                    <div className="analysis-content">
                                        <h6>Média Diária</h6>
                                        <div className="analysis-value">
                                            <strong>
                                                {reportData?.evolucaoDiaria?.length > 0 
                                                    ? Math.round(totalAgendamentos / reportData.evolucaoDiaria.length)
                                                    : 0
                                                } agendamentos/dia
                                            </strong>
                                            <div className="analysis-meta">
                                                {reportData?.evolucaoDiaria?.length || 0} dias analisados
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="analysis-item">
                                    <div className="analysis-icon">
                                        <Users size={20} className="text-primary" />
                                    </div>
                                    <div className="analysis-content">
                                        <h6>Dia com Mais Agendamentos</h6>
                                        <div className="analysis-value">
                                            <strong>
                                                {reportData?.evolucaoDiaria?.length > 0 
                                                    ? format(new Date(reportData.evolucaoDiaria.reduce((prev, current) => 
                                                        (prev.count > current.count) ? prev : current
                                                    )._id), 'dd/MM/yyyy')
                                                    : 'N/A'
                                                }
                                            </strong>
                                            <div className="analysis-meta">
                                                {reportData?.evolucaoDiaria?.length > 0 
                                                    ? reportData.evolucaoDiaria.reduce((prev, current) => 
                                                        (prev.count > current.count) ? prev : current
                                                    ).count + ' agendamentos'
                                                    : 'Sem dados'
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="analysis-item">
                                    <div className="analysis-icon">
                                        <Monitor size={20} className="text-primary" />
                                    </div>
                                    <div className="analysis-content">
                                        <h6>Status Mais Comum</h6>
                                        <div className="analysis-value">
                                            <strong>
                                                {reportData?.resumoStatus?.length > 0 
                                                    ? reportData.resumoStatus.reduce((prev, current) => 
                                                        (prev.count > current.count) ? prev : current
                                                    )._id
                                                    : 'N/A'
                                                }
                                            </strong>
                                            <div className="analysis-meta">
                                                {reportData?.resumoStatus?.length > 0 
                                                    ? reportData.resumoStatus.reduce((prev, current) => 
                                                        (prev.count > current.count) ? prev : current
                                                    ).count + ' ocorrências'
                                                    : 'Sem dados'
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 🔥 DETALHAMENTO POR STATUS */}
                    {reportData?.resumoStatus && reportData.resumoStatus.length > 0 && (
                        <div className="stat-detail-card" style={{ marginTop: '1.5rem' }}>
                            <h6>Detalhamento por Status</h6>
                            <div className="row">
                                {reportData.resumoStatus.map((status, index) => (
                                    <div key={status._id} className="col-md-4 mb-2">
                                        <div className="stat-detail-item">
                                            <span className="status-badge">{status._id}</span>
                                            <strong>{status.count}</strong>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 🔥 ESTADO VAZIO */}
                    {!isLoading && totalAgendamentos === 0 && (
                        <div className="empty-state" style={{ marginTop: '2rem' }}>
                            <CalendarClock size={48} className="text-muted mb-3" />
                            <h5>Nenhum agendamento encontrado</h5>
                            <p>
                                Não foram encontrados agendamentos para os filtros selecionados no período de 
                                {appliedFilters.startDate && ` ${format(new Date(appliedFilters.startDate), 'dd/MM/yyyy')}`} 
                                {appliedFilters.endDate && ` a ${format(new Date(appliedFilters.endDate), 'dd/MM/yyyy')}`}.
                            </p>
                            <button
                                onClick={handleClearFilters}
                                className="action-button action-button-primary"
                            >
                                Limpar Filtros
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AgendamentosReport;